import { Constants } from "@babylonjs/core/Engines/constants";
import { Material } from "@babylonjs/core/Materials/material";
import { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { _GetCompatibleTextureLoader } from "@babylonjs/core/Materials/Textures/Loaders/textureLoaderManager";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MaterialBuilderBase, MmdMaterialRenderMethod } from "./materialBuilderBase";
import { PmxObject } from "./Parser/pmxObject";
/**
 * PBR material builder
 *
 * Use `PBRMaterial` to create a mmdmesh material
 */
export class PBRMaterialBuilder extends MaterialBuilderBase {
    constructor() {
        super(PBRMaterial);
    }
    _getForcedExtension(texturePath) {
        if (texturePath.substring(texturePath.length - 4).toLowerCase() === ".bmp") {
            if (_GetCompatibleTextureLoader(".dxbmp") !== null) {
                return ".dxbmp";
            }
        }
        return undefined;
    }
    loadGeneralScalarProperties(material, materialInfo, meshes) {
        const diffuse = materialInfo.diffuse;
        material.albedoColor = new Color3(diffuse[0], diffuse[1], diffuse[2]);
        const specular = materialInfo.specular;
        material.reflectionColor = new Color3(specular[0], specular[1], specular[2]);
        const ambient = materialInfo.ambient;
        material.ambientColor = new Color3(ambient[0], ambient[1], ambient[2]);
        const alpha = materialInfo.diffuse[3];
        material.alpha = alpha;
        if (alpha === 0) {
            for (let i = 0; i < meshes.length; ++i) {
                const mesh = meshes[i];
                if (mesh.isVisible !== undefined) {
                    mesh.isVisible = false;
                }
                else {
                    // TODO: handle visibility of submeshes individually
                }
            }
        }
        material.metallic = 0.0;
        material.roughness = materialInfo.shininess / 100.0;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    async loadDiffuseTexture(uniqueId, material, materialInfo, imagePathTable, textureInfo, scene, assetContainer, rootUrl, referenceFileResolver, logger, onTextureLoadComplete) {
        material.backFaceCulling = (materialInfo.flag & PmxObject.Material.Flag.IsDoubleSided) ? false : true;
        const diffuseTexturePath = imagePathTable[textureInfo?.imagePathIndex ?? -1];
        if (diffuseTexturePath !== undefined) {
            const diffuseTextureFileFullPath = referenceFileResolver.createFullPath(diffuseTexturePath);
            let texture;
            const file = referenceFileResolver.resolve(diffuseTextureFileFullPath);
            if (file !== undefined) {
                texture = await this._textureLoader.loadTextureFromBufferAsync(uniqueId, diffuseTextureFileFullPath, file instanceof File ? file : file.data, scene, assetContainer, {
                    ...textureInfo,
                    deleteBuffer: this.deleteTextureBufferAfterLoad,
                    format: Constants.TEXTUREFORMAT_RGBA,
                    mimeType: file instanceof File ? file.type : file.mimeType,
                    forcedExtension: this._getForcedExtension(diffuseTexturePath)
                });
            }
            else {
                texture = await this._textureLoader.loadTextureAsync(uniqueId, rootUrl, diffuseTexturePath, scene, assetContainer, {
                    ...textureInfo,
                    deleteBuffer: this.deleteTextureBufferAfterLoad,
                    format: Constants.TEXTUREFORMAT_RGBA,
                    forcedExtension: this._getForcedExtension(diffuseTexturePath)
                });
            }
            const diffuseTexture = texture;
            if (diffuseTexture !== null) {
                material.albedoTexture = diffuseTexture;
            }
            else {
                logger.error(`Failed to load diffuse texture: ${diffuseTextureFileFullPath}`);
            }
            onTextureLoadComplete?.();
        }
        else {
            onTextureLoadComplete?.();
        }
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    async setAlphaBlendMode(material, materialInfo, meshes, logger, getTextureAlphaChecker) {
        if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlending) {
            if (material.albedoTexture) {
                material.albedoTexture.hasAlpha = true;
                material.useAlphaFromAlbedoTexture = true;
            }
            material.transparencyMode = Material.MATERIAL_ALPHABLEND;
            material.forceDepthWrite = true;
            return;
        }
        if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation) {
            if (material.alpha < 1) {
                if (material.albedoTexture) {
                    material.albedoTexture.hasAlpha = true;
                    material.useAlphaFromAlbedoTexture = true;
                }
                material.transparencyMode = Material.MATERIAL_ALPHABLEND;
                material.forceDepthWrite = true;
                return;
            }
        }
        const diffuseTexture = material.albedoTexture;
        const evaluatedTransparency = materialInfo.evaluatedTransparency ?? -1;
        if (diffuseTexture !== null) {
            const transparencyMode = await this._evaluateDiffuseTextureTransparencyModeAsync(diffuseTexture, evaluatedTransparency, meshes, logger, getTextureAlphaChecker);
            if (transparencyMode !== null) {
                const hasAlpha = transparencyMode !== Material.MATERIAL_OPAQUE;
                if (hasAlpha)
                    diffuseTexture.hasAlpha = true;
                material.useAlphaFromAlbedoTexture = hasAlpha;
                material.transparencyMode = transparencyMode;
                if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation) {
                    material.forceDepthWrite = hasAlpha;
                }
            }
        }
        else {
            if (this.renderMethod === MmdMaterialRenderMethod.DepthWriteAlphaBlendingWithEvaluation) {
                let etIsNotOpaque = (evaluatedTransparency >> 4) & 0x03;
                if ((etIsNotOpaque ^ 0x03) === 0) { // 11: not evaluated
                    etIsNotOpaque = 0; // fallback to opaque
                }
                material.transparencyMode = etIsNotOpaque === 0 ? Material.MATERIAL_OPAQUE : Material.MATERIAL_ALPHABLEND;
            }
            else /* if (this.renderMethod === MmdStandardMaterialRenderMethod.AlphaEvaluation) */ {
                let etAlphaEvaluateResult = evaluatedTransparency & 0x0F;
                if ((etAlphaEvaluateResult ^ 0x0F) === 0) { // 1111: not evaluated
                    etAlphaEvaluateResult = 0; // fallback to opaque
                }
                material.transparencyMode = Material.MATERIAL_OPAQUE;
            }
        }
    }
    _buildTextureNameMap(materialsInfo, materials, imagePathTable, texturesInfo, textureNameMap) {
        for (let i = 0; i < materialsInfo.length; ++i) {
            const materialInfo = materialsInfo[i];
            const material = materials[i];
            const diffuseTexturePath = imagePathTable[texturesInfo[materialInfo.textureIndex]?.imagePathIndex];
            if (diffuseTexturePath !== undefined) {
                const diffuseTexture = material.albedoTexture;
                if (diffuseTexture !== null) {
                    textureNameMap.set(diffuseTexture, diffuseTexturePath);
                }
            }
        }
    }
    loadSphereTexture() { }
    loadToonTexture() { }
    loadOutlineRenderingProperties() { }
}
